/**
 * useClassificationMenu Hook
 *
 * Bundles the classification lookup + action menu state into a single hook
 * so that useModerationWorkQueueSetup stays under the max-statements limit.
 *
 * Provides the "..." action menu state (open/close/toggle) and wires the
 * "Classification" action to call useClassificationLookup with the
 * focused case's subjectId.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ModerationCase } from '../domain/ModerationCase';

import { useClassificationLookup } from './useClassificationLookup';

/**
 * Base prefix for the action menu trigger ID.
 * The full ID is made unique per focused case so the design-system Menu
 * component's internal `useEffect([triggerId, isOpen])` re-runs when
 * card focus changes — keeping `triggerElementRef` in sync with the
 * newly mounted IconButton before the menu opens.
 */
const ACTION_MENU_TRIGGER_PREFIX = 'moderation-action-menu-trigger';

/** Parameters for the classification menu hook */
interface ClassificationMenuParams {
  communityId: string;
  sessionCases: ModerationCase[];
  focusedCaseId: string | null;
}

/** All state & handlers the page needs for the action menu + classification dialog */
export interface ClassificationMenuState {
  isActionMenuOpen: boolean;
  actionMenuTriggerId: string;
  handleActionMenuToggle: () => void;
  handleActionMenuClose: () => void;
  handleClassification: () => void;
  classificationDialogData: ReturnType<typeof useClassificationLookup>['classificationDialogData'];
  isClassificationDialogOpen: boolean;
  isClassificationSearching: boolean;
  classificationSearchCompleted: boolean;
  classificationErrors: ReturnType<typeof useClassificationLookup>['errors'];
  isSafe: boolean;
  isContentBlurred: boolean;
  closeClassificationDialog: () => void;
  toggleClassificationBlur: () => void;
}

/**
 * Hook that combines the action menu open/close state with the classification
 * lookup triggered from that menu.
 */
export function useClassificationMenu({ communityId, sessionCases, focusedCaseId }: ClassificationMenuParams): ClassificationMenuState {
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  /**
   * Build a unique trigger ID per focused case so the Menu component
   * re-discovers the correct DOM element whenever card focus changes.
   * Without this, the internal `triggerElementRef` becomes stale after
   * the old IconButton unmounts and a new one mounts on a different card,
   * causing the menu to render at (0,0) — effectively invisible.
   */
  const actionMenuTriggerId = useMemo(() => (focusedCaseId ? `${ACTION_MENU_TRIGGER_PREFIX}-${focusedCaseId}` : ACTION_MENU_TRIGGER_PREFIX), [focusedCaseId]);

  // Close the action menu whenever the focused card changes so the user
  // always starts with a clean slate on the newly focused card.
  useEffect(() => {
    setIsActionMenuOpen(false);
  }, [focusedCaseId]);

  const {
    lookupClassification,
    classificationDialogData,
    isDialogOpen: isClassificationDialogOpen,
    isSearching: isClassificationSearching,
    searchCompleted: classificationSearchCompleted,
    errors: classificationErrors,
    isSafe,
    isContentBlurred,
    closeDialog: closeClassificationDialog,
    toggleBlur: toggleClassificationBlur,
  } = useClassificationLookup();

  const handleActionMenuToggle = useCallback(() => {
    setIsActionMenuOpen(prev => !prev);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setIsActionMenuOpen(false);
  }, []);

  const handleClassification = useCallback(() => {
    const focused = sessionCases.find(c => c.caseId === focusedCaseId);

    if (communityId && focused?.subjectId) {
      lookupClassification(communityId, focused.subjectId);
    }
  }, [communityId, sessionCases, focusedCaseId, lookupClassification]);

  return {
    isActionMenuOpen,
    actionMenuTriggerId,
    handleActionMenuToggle,
    handleActionMenuClose,
    handleClassification,
    classificationDialogData,
    isClassificationDialogOpen,
    isClassificationSearching,
    classificationSearchCompleted,
    classificationErrors,
    isSafe,
    isContentBlurred,
    closeClassificationDialog,
    toggleClassificationBlur,
  };
}
